/* eslint-disable @typescript-eslint/member-ordering */
import { action, runInAction, observable, toJS, makeObservable } from 'mobx';
import { SessionService } from '../services';
import {
    FormInputModels,
    IotaAppComment,
    IotaSessionResult,
    SessionCreationReason,
    SessionState,
    SessionStates,
    TabApplicationData,
    SessionFilter
} from '../services/types';
import { types } from '../services';
import { DynamicUiModel } from './DynamicUiModel';
import { IotaSessionsStore, UserProfileStore } from '.';
import { Subject } from 'rxjs';
import { message } from 'antd';
import { TabModel } from '../types/TabModel';

const GET_SESSION_STATE_RETRY_COUNT = 1000;
const GET_SESSION_SATE_TIMEOUT = 500;

export default class WorkflowInstanceStore {
    isPdfHandleInProgress: boolean = false;

    sessionId: string;

    dynamicUi: DynamicUiModel;

    applicationName: string = '';

    // TODO: Make dedicated store and view for scaned results
    renderScanResults: boolean = false;

    sessionGetRetryCount: number = 0;

    reloadInProgress: boolean = false;

    sessionToComplete: string;

    sessionExtension: { [sessionId: string]: string } = {};

    uiUpdateSubject = new Subject<DynamicUiModel | undefined>();

    preservedAppData: TabApplicationData;

    constructor(
        public sessionService: SessionService,
        private iotaSessionsStore: IotaSessionsStore,
        private userProfileStore: UserProfileStore
    ) {
        makeObservable(this, {
            isPdfHandleInProgress: observable,
            sessionId: observable,
            dynamicUi: observable,
            applicationName: observable,
            reloadInProgress: observable,
            setApplicationName: action.bound,
            setReloadInProgress: action,
            startSession: action.bound,
            startSessionForPackageSet: action.bound,
            startSessionForMultiplePackages: action.bound,
            reload: action,
            reloadPackageSession: action.bound,
            getSessionState: action.bound,
            continueSession: action,
            loadSessionUiModel: action.bound,
            createUiModel: action,
            getUiModel: action,
            handleFormSubmit: action,
            loadApplicationSessions: action,
            switchToSession: action,
            getFileFromIotaTempStorage: action.bound,
            getSessionUiModel: action
        });

        this.iotaSessionsStore.iotaSessionChanges.subscribe((s: IotaSessionResult) => {
            if (this.sessionExtension[s.sessionId]) {
                this.loadSessionUiModel(s.sessionId, s.response, s.inProgress);
            }
        });
    }

    setApplicationName(name: string) {
        this.applicationName = name;
    }

    setReloadInProgress(val: boolean) {
        this.reloadInProgress = val;
    }

    async startSession(
        packageId: string,
        appId: string,
        packageName: string,
        extension: string,
        callback?: (sessionId: string, packageId: string, packageName: string) => void,
        replacesSessionId?: string,
        creationReason?: SessionCreationReason
    ) {
        this.isPdfHandleInProgress = true;
        this.renderScanResults = true;

        const resp = await this.sessionService.startSession(appId, packageId, replacesSessionId, creationReason);
        if (resp.isErr()) {
            runInAction(() => {
                this.isPdfHandleInProgress = false;
                this.reloadInProgress = false;
            });
            message.error(resp.error.data?.title || resp.error.text);
            return false;
        }
        resp.map(sess => {
            runInAction(() => {
                this.sessionId = sess.sessionId;
            });

            if (callback) {
                callback(sess.sessionId, packageId, packageName);
            }

            if (extension) {
                this.sessionExtension[this.sessionId] = extension;
            }
        });
        return true;
    }

    async startSessionForPackageSet(
        packageSetId: string,
        appId: string,
        extension: string | undefined,
        setName: string,
        callback?: (sessionId: string, setId: string, setName: string) => void,
        isBulk: boolean = true
    ) {
        this.isPdfHandleInProgress = true;
        this.renderScanResults = true;

        const resp = await this.sessionService.startSessionForPackageSet(appId, packageSetId, isBulk);
        resp.map(data => {
            const sessionId = data && data.length ? data[0] : '';

            runInAction(() => {
                this.sessionId = sessionId;
            });

            if (callback) {
                callback(sessionId, setName, packageSetId);
            }

            if (extension) {
                this.sessionExtension[this.sessionId] = extension;
            }
        });
    }

    async startSessionForMultiplePackages(
        packageIds: string[],
        appId: string,
        extension: string,
        callback?: (sessionId: string) => void
    ) {
        this.isPdfHandleInProgress = true;
        this.renderScanResults = true;

        const resp = await this.sessionService.startSessionForMultiplePackages(appId, packageIds);
        resp.map(data => {
            runInAction(() => {
                this.sessionId = data.sessionId;
            });

            if (callback) {
                callback(data.sessionId);
            }

            if (extension) {
                this.sessionExtension[this.sessionId] = extension;
            }
        });
    }

    async reload(
        pkgId: string,
        appId: string,
        sessionId: string,
        packageName: string,
        extension: string,
        callback?: (sessionId: string, packageId: string, packageName: string) => void
    ) {
        this.sessionToComplete = sessionId;
        this.setReloadInProgress(true);
        await this.startSession(pkgId, appId, packageName, extension, callback, sessionId, 'ReRunAnalysis');
    }

    async reloadPackageSession(
        sessionId: string,
        pkgSetId: string,
        appId: string,
        extension: string,
        setName: string,
        callback?: (sessionId: string, setName: string, setId: string) => void,
        isBulk: boolean = true
    ) {
        this.sessionToComplete = sessionId;
        this.setReloadInProgress(true);
        await this.startSessionForPackageSet(pkgSetId, appId, extension, setName, callback, isBulk);
    }

    async updateSession(sessionId: string, state: SessionStates) {
        return this.sessionService.updateSession(sessionId, state);
    }

    async getSessionState(
        runtimeSessionId: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resolve: any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        reject: any,
        extension: string | undefined = undefined
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): Promise<any> {
        if (!runtimeSessionId) {
            return;
        }

        setTimeout(async () => {
            let resp = !extension
                ? await this.sessionService.getSessionState(runtimeSessionId)
                : await this.sessionService.getSessionStateFromIota(runtimeSessionId, extension);
            const that = this;

            resp.mapErr(() => {
                if (this.sessionGetRetryCount < GET_SESSION_STATE_RETRY_COUNT) {
                    this.sessionGetRetryCount++;
                    return that.getSessionState(runtimeSessionId, resolve, reject, extension);
                } else {
                    return reject();
                }
            }).map(state => {
                if (!state && this.sessionGetRetryCount < GET_SESSION_STATE_RETRY_COUNT) {
                    this.sessionGetRetryCount++;
                    return this.getSessionState(runtimeSessionId, resolve, reject, extension);
                } else if (!state.inProgress) {
                    this.sessionGetRetryCount = 0;
                    return resolve(state);
                } else if (!this.isPdfHandleInProgress) {
                    this.sessionGetRetryCount = 0;
                    return resolve(state);
                } else if (this.sessionGetRetryCount < GET_SESSION_STATE_RETRY_COUNT) {
                    this.sessionGetRetryCount++;
                    return this.getSessionState(runtimeSessionId, resolve, reject, extension);
                } else {
                    this.sessionGetRetryCount = 0;
                    return resolve();
                }
            });
        }, GET_SESSION_SATE_TIMEOUT);
    }

    async continueSession(values: {}, actionId: string, sessionId: string, extension: string) {
        this.dynamicUi?.setInProgress(true);
        this.isPdfHandleInProgress = true;

        this.sessionExtension[sessionId] = extension;

        let errorMessage: string;
        try {
            const result = await this.sessionService.continueSession(values, actionId, sessionId);
            if (result.isOk()) {
                return true;
            }
            errorMessage = result.error.data?.title || result.error.text;
        } catch (error) {
            errorMessage = error instanceof Error ? error.message : 'Unable to continue the session. Please try again.';
        }

        runInAction(() => {
            this.dynamicUi?.setInProgress(false);
            this.isPdfHandleInProgress = false;
            delete this.sessionExtension[sessionId];
        });
        message.error(errorMessage);
        return false;
    }

    async loadSessionUiModel(
        runtimeSessionId: string,
        response: FormInputModels | undefined = undefined,
        inProgress: boolean = false
    ) {
        const extension = this.sessionExtension[runtimeSessionId];
        if (extension) {
            this.uiUpdateSubject.next(undefined);

            if (!this.isPdfHandleInProgress) {
                return;
            }

            if (response) {
                this.dynamicUi = new DynamicUiModel(response, this.sessionService, this.userProfileStore, this);
                this.dynamicUi.setWidgetCurrentValues();
                this.dynamicUi.setApplicationData(this.preservedAppData);
                this.dynamicUi.setInProgress(inProgress);
                await this.dynamicUi.loadValuesForWidgets(this.sessionId);
            } else {
                const res = await this.sessionService.getSessionStateFromIota(runtimeSessionId, extension);
                await res.asyncMap(async state => {
                    await this.createUiModel(state);
                });
            }

            this.uiUpdateSubject.next(this.dynamicUi);

            if (!inProgress) {
                runInAction(() => (this.isPdfHandleInProgress = false));
                delete this.sessionExtension[runtimeSessionId];
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async createUiModel(state: any) {
        this.dynamicUi = new DynamicUiModel(state.response, this.sessionService, this.userProfileStore, this);
        this.dynamicUi.setWidgetCurrentValues();
        this.dynamicUi.setApplicationData(this.preservedAppData);
        await this.dynamicUi.loadValuesForWidgets(this.sessionId);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getUiModel(state: any, applicationData: types.TabApplicationData) {
        const dynamicUi = new DynamicUiModel(state.response, this.sessionService, this.userProfileStore, this);
        dynamicUi.setApplicationData(applicationData);
        dynamicUi.setWidgetCurrentValues();
        this.preservedAppData = toJS(applicationData);
        await dynamicUi.loadValuesForWidgets(this.sessionId);
        dynamicUi.setValidateOnLoad(dynamicUi.uiModel.inputs.some(i => i.behavior?.validationAction));
        return dynamicUi;
    }

    async handleFormSubmit(widgetValues: {}, actionid: string, tab: TabModel) {
        const tabMetadata = tab.metadata;
        const ui: DynamicUiModel = tabMetadata.dynamicUI;
        if (!ui.actions.some(uiAction => uiAction.id === actionid)) {
            tabMetadata.freezeUi = true;
        }
        const accepted = await this.continueSession(
            widgetValues,
            actionid,
            tabMetadata.sessionId,
            tabMetadata.applicationData.appExtension
        );
        if (!accepted) {
            runInAction(() => {
                tab.metadata.freezeUi = false;
                tab.metadata.uiIsLoading = false;
                tab.metadata.dynamicUI.setInProgress(false);
            });
        }
    }

    async loadApplicationSessions(filter?: SessionFilter) {
        if (filter && !this.userProfileStore.hasAccessToAllEntities) {
            filter.userId = this.userProfileStore.currentUserProfileInfo.userId;
        }

        return await this.sessionService.getSessionsForProjects(filter);
    }

    async switchToSession(sessionId: string, extension: string) {
        runInAction(() => {
            this.renderScanResults = true;
            this.isPdfHandleInProgress = true;
            this.sessionId = sessionId;
        });

        const state: SessionState = await new Promise((resolve, reject) => {
            this.getSessionState(sessionId, resolve, reject, extension);
        });

        if (state) {
            await this.createUiModel(state);
        }

        runInAction(() => {
            this.isPdfHandleInProgress = false;
        });
    }

    async getFileFromIotaTempStorage(path: string) {
        await this.sessionService.getFileFromIotaTempStorage(path);
    }

    async getSessionUiModel(runtimeSessionId: string, sessionId: string, applicationData: types.TabApplicationData) {
        const { appExtension } = applicationData;
        runInAction(() => {
            this.renderScanResults = true;
            this.isPdfHandleInProgress = true;
            this.sessionId = sessionId;
        });
        // const state: SessionState = await new Promise((resolve, reject) => {

        const promises = [
            new Promise((resolve, reject) => {
                this.getSessionState(runtimeSessionId, resolve, reject, appExtension);
            }),
            this.sessionService.getComments(sessionId)
        ] as [Promise<SessionState>, Promise<IotaAppComment[]>];
        const res = await Promise.all(promises);
        let uiModel: DynamicUiModel | undefined = undefined;
        if (res[0]) {
            uiModel = await this.getUiModel(res[0], applicationData);
            uiModel.setComments(res[1]);
        }

        runInAction(() => {
            this.isPdfHandleInProgress = false;
        });

        return uiModel;
    }
}
